import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cad-arc';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { useAssociateEventsApi } from '../hooks/use-associate-events-api';
import { AssociateEntityForm, AssociateEntityFormDisplayType } from './associate-entity-form';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';

export type AssociateEventModalComponentType = typeof AssociateEventModal;

export interface AssociateEventModalProps {
    agencyEventId: number;
    portalProps?: ModalProps['portalProps'];
    onSuccess?: (...args: unknown[]) => unknown;
}

export const AssociateEventModal: React.FC<AssociateEventModalProps> = ({
    agencyEventId,
    portalProps,
    onSuccess,
}) => {
    const modalControls = useFullscreenModalControls();

    const onCancel = React.useCallback(
        function onCancel(): void {
            modalControls.close();
        },
        [modalControls]
    );

    const handleSuccess = useCallback(() => {
        onSuccess?.();
        modalControls.close();
    }, [onSuccess, modalControls]);

    const { mutation } = useAssociateEventsApi(agencyEventId, handleSuccess);

    const form = useForm<AssociateEntityFormDisplayType>({
        mode: 'onBlur',
        defaultValues: {
            entityId: agencyEventId,
            associatedEntities: [],
            reason: '',
        },
    });

    const associatedEvents = form.watch('associatedEntities');

    return (
        <IntegratedFormProvider mutation={mutation} form={form}>
            <BlankModal
                portalProps={portalProps}
                size="md"
                onClose={onCancel}
                isOpen={true}
                className="associate-event-modal"
            >
                <ModalHeader>Associate Event</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <FormProvider {...form}>
                            <AssociateEntityForm entityType="EVENT" />
                        </FormProvider>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={onCancel} />
                        <FormSaveButton disabled={!associatedEvents.length}>
                            Associate Events
                        </FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
